import { Backdrop, Box, Button, CircularProgress, TextField } from '@mui/material';
import Header from '../../components/Header';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import CustomTable from '../../components/CustomTable';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { greenColor } from '../../config/colors';
import { useNavigate } from 'react-router';

const StatusBadge = ({ value }) => {
  const { t } = useTranslation();
  const config = {
    ACTIVE: {
      label: t('resale.statuses.active'),
      dotColor: '#BDBDBD',
      color: '#9E9E9E',
      bg: '#F5F5F5',
    },
    PENDING: {
      label: t('resale.statuses.pending'),
      dotColor: '#F5A623',
      color: '#F5A623',
      bg: '#FFF8EC',
    },
    APPROVED: {
      label: t('resale.statuses.approved'),
      dotColor: '#4CAF50',
      color: '#4CAF50',
      bg: '#F0FAF0',
    },
    REJECTED: {
      label: t('resale.statuses.rejected'),
      dotColor: '#F44336',
      color: '#F44336',
      bg: '#FFF0EE',
    },
  };

  const c = config[value] ?? {
    label: value ?? '-',
    dotColor: '#BDBDBD',
    color: '#9E9E9E',
    bg: '#F5F5F5',
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '12px',
        fontWeight: 500,
        px: '10px',
        py: '3px',
        borderRadius: '20px',
        color: c.color,
        backgroundColor: c.bg,
        lineHeight: 1,
      }}
    >
      <Box
        component="span"
        sx={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          backgroundColor: c.dotColor,
          flexShrink: 0,
          display: 'inline-block',
        }}
      />
      {c.label}
    </Box>
  );
};

const Resale = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [resales, setResales] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setIsLoading] = useState();

  const fetchResales = useCallback(
    async (term = '') => {
      try {
        setIsLoading(true);
        const { data } = await api.get('/v1/resale/list', { params: { name: term, pageSize: 300 } });
        setResales(data?.list);
      } catch (error) {
        console.error(t('messages.errorFetchingData'), error);
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchResales();
  }, [fetchResales]);

  const columns = [
    {
      field: 'nameCompany',
      headerName: t('resale.nameCompany'),
      render: (value, row) => (
        <Box>
          <Box
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'uppercase',
              color: colors.grey[200],
            }}
          >
            {row.user?.name ?? '-'}
          </Box>
          <Box
            sx={{
              fontSize: '12px',
              color: colors.grey[400],
              textTransform: 'uppercase',
              mt: '2px',
            }}
          >
            {row.company?.name ?? '-'}
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: t('forms.email'),
      render: (value, row) => (
        <span style={{ fontSize: '13px', color: colors.grey[300] }}>
          {row.user?.username ?? row.company?.email ?? '-'}
        </span>
      ),
    },
    {
      field: 'created_at',
      headerName: t('forms.createdAt') || 'Criado em',
      render: (value) => (
        <span style={{ fontSize: '13px', color: colors.grey[300] }}>
          {value ? new Date(value).toLocaleDateString('pt-BR') : '-'}
        </span>
      ),
    },
    {
      field: 'status',
      headerName: t('resale.status'),
      render: (value, row) => {
        const stage = row.company?.access_stage;
        const resolved = !stage || stage === 'NONE' ? 'ACTIVE' : stage;
        return <StatusBadge value={resolved} />;
      },
    },
  ];

  const actions = [
    {
      icon: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
      tooltip: 'Detalhes',
      onClick: (row) => navigate(`${row.id}`),
    },
    {
      icon: <SettingsOutlinedIcon sx={{ fontSize: 18 }} />,
      tooltip: 'Configurações',
      onClick: (row) => navigate(`${row.id}/settings`),
    },
  ];

  if (loading) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box m="20px">
      <Header title={t('resale.title')} subtitle={t('resale.subtitle')} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TextField
          placeholder={t('forms.searchByName')}
          size="small"
          value={searchTerm}
          onChange={(e) => {
            const value = e.target.value;
            setSearchTerm(value);
            if (value === '') fetchResales();
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') fetchResales(searchTerm);
          }}
          sx={{ flex: 1, maxWidth: 340 }}
        />

        <Button
          variant="contained"
          onClick={() => fetchResales(searchTerm)}
          sx={{
            bgcolor: greenColor,
            color: '#000',
            '&:hover': { bgcolor: '#6ec13b' },
            fontWeight: 700,
          }}
        >
          {t('buttons.consult').toUpperCase()}
        </Button>
      </Box>

      {/* ── Tabela ── */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          px: '20px',
          pb: '4px',
        }}
      >
        <CustomTable
          columns={columns}
          rows={resales}
          actions={actions}
          emptyMessage={t('resale.resaleNotFound')}
          elevation={0}
        />
      </Box>
    </Box>
  );
};

export default Resale;
